import { Html } from '@react-three/drei';
import { axialToWorld } from '@/game/hexmap';
import config from '@/game/hexmap/config';
import { type Cell, territoryOwners } from '@/game/sim';
import { cityIntel, fogCityMarkers } from '@/game/view/lastSeen';
import { colorIndexFor, PLAYER_COLORS } from '@/game/view/palette';
import { useSimFrame } from './app/simFrame';
import { HTML_Z_RANGE } from './ConstructionLabels';
import { intelText } from './TileInfoPanel';

const MARKER_HEIGHT = 0.9;

interface RivalCityMarkersProps {
  /**
   * A press on a marker, handled as a press on its city's tile (right-click
   * attacks the remembered city, left-click pins its tile info).
   */
  onPointerDown?: (pos: Cell, event: PointerEvent) => void;
}

/**
 * A small castle icon ringed in the owner's colour over each standing rival
 * city the player has seen but can't see right now (its model only draws in
 * sight), so they can be found and aimed at through the fog. Live like their
 * borders: one destroyed out of sight loses its marker with its border.
 * Each shows the city's level as last seen, and its tooltip the defence the
 * player knows of (`cityIntel`: level and hp as last seen, soldiers in sight).
 * Each marker takes the clicks over it (a padded hit area around the icon),
 * so a click on it never falls through to the tile behind it on screen.
 */
export function RivalCityMarkers({ onPointerDown }: RivalCityMarkersProps) {
  const frame = useSimFrame();
  const { playerId, playerOrder, rules } = frame;
  const known = frame.knownBuildings();
  const cities = fogCityMarkers(frame.view, rules, known, playerId, frame.fog.sight);
  const owners = cities.length > 0 ? territoryOwners(frame.view, rules) : undefined;
  return (
    <>
      {cities.map((city) => {
        const world = axialToWorld(city.pos, config.hexMap.tile.radius);
        const color = PLAYER_COLORS[colorIndexFor(city.owner, playerOrder)];
        const intel = cityIntel(
          frame.view,
          rules,
          known,
          playerId,
          city.id,
          frame.fog.sight,
          owners
        );
        return (
          <Html
            key={city.id}
            position={[world.x, MARKER_HEIGHT, world.z]}
            center
            zIndexRange={HTML_Z_RANGE}
            style={{ pointerEvents: onPointerDown ? 'auto' : 'none' }}
          >
            <div
              title={`Rival city${intel ? `: ${intelText(intel)}` : ' (as last seen)'}. Right-click to attack`}
              onPointerDown={(e) => {
                // Keep the press from bubbling on to R3F's listener on the
                // canvas wrapper, which would raycast the tile behind the
                // marker on screen and take the press for that tile instead.
                e.stopPropagation();
                onPointerDown?.(city.pos, e.nativeEvent);
              }}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full"
            >
              <div
                className="relative flex h-6 w-6 items-center justify-center rounded-full bg-surface-strong text-xs opacity-90 shadow"
                style={{ border: `2px solid ${color}` }}
              >
                🏰
                {intel && (
                  <span className="absolute -bottom-1 -right-1.5 rounded-full bg-surface-strong px-1 text-[9px] font-semibold leading-3 tabular-nums text-fg">
                    {intel.level}
                  </span>
                )}
              </div>
            </div>
          </Html>
        );
      })}
    </>
  );
}
